import { useCallback, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, XCircle, Send, X } from 'lucide-react';
import { useWebSocketEvent } from '@/hooks/use-websocket';
import { useEventsStore, type AlarmEvent } from '@/store/events-store';
import { useVehiclesStore } from '@/store/vehicles-store';
import { playAlarmSound } from '@/lib/alarm-sound';
import { alarmName } from '@/lib/alarm-names';
import { alarmVisual } from '@/lib/alarm-icon';
import { objectTypeToShape } from '@/lib/vehicle-icon';
import { formatDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { WsMessage } from '@/lib/ws';

// =============================================================================
// EventNotifier — berlangganan WS event 'alarm' (atau 'gps' dengan alarm > 0)
// dan 'response' (feedback command), lalu:
//   1. Push ke events store (semua event tersimpan)
//   2. Kalau lolos filter → popup + alarm sound (Web Audio beep)
//
// Popup TIDAK memakai sonner: dirender sebagai stack sendiri (lihat return)
// supaya tidak saling overlap — tertata MENURUN dari kanan-atas, dan saat
// kolom penuh (max-height layar) lanjut ke kolom baru DI KIRI-nya
// (flex-col + flex-wrap-reverse), sehingga semua notifikasi tetap terbaca.
//
// Mount sekali di AppLayout supaya selalu aktif selama user login.
// =============================================================================

// Maksimum popup yang ditahan bersamaan — melebihi ini yang paling lama dibuang
// (events store tetap menyimpan semuanya; ini hanya batas tampilan).
const MAX_STACK = 30;
const POPUP_DURATION_MS = 10_000;

interface StackItem {
  id: number;
  render: (dismiss: () => void) => ReactNode;
}

interface AlarmWsPayload extends WsMessage {
  vh_id?: number;
  vehicle_id?: number;
  vehicle_name?: string;
  real_name?: string;
  nopol?: string;
  alarm?: number;
  alarm_id?: number;
  alarm_name?: string;
  lat?: number;
  lng?: number;
  tdate?: string;
  timestamp?: string;
  /** Nama geofence dari payload posisi (Masuk/Keluar Geofence). */
  gf?: string;
  /** Alamat dari payload posisi (bila backend sudah isi). */
  address?: string;
}

function extractEvent(
  msg: AlarmWsPayload,
  getVehicleName: (id: number) => string,
): AlarmEvent | null {
  const vehicleId = msg.vh_id ?? msg.vehicle_id ?? 0;
  const alarmId = msg.alarm_id ?? msg.alarm ?? 0;
  if (!vehicleId || !alarmId) return null;

  const lat = Number(msg.lat ?? 0);
  const lng = Number(msg.lng ?? 0);
  if (!isFinite(lat) || !isFinite(lng)) return null;

  const ts = msg.timestamp ?? msg.tdate ?? new Date().toISOString();
  return {
    id: `${vehicleId}-${ts}-${alarmId}`,
    vehicleId,
    // real_name di-skip — backend kadang isi dengan nama pemilik, bukan
    // alias kendaraan. Fallback ke getVehicleName (lookup ke vehiclesStore
    // pakai nopol) lalu #id sebagai last resort.
    vehicleName: msg.nopol || msg.vehicle_name || getVehicleName(vehicleId) || `#${vehicleId}`,
    alarmId,
    alarmName: alarmName(alarmId, msg.alarm_name),
    lat,
    lng,
    geofence: msg.gf || undefined,
    address: msg.address || undefined,
    timestamp: ts,
  };
}

// Map alarm color (Tailwind text class) → bg class untuk header bar toast.
// Pakai colored stripe atas mirip referensi (red/amber/blue/green bar).
function alarmHeaderBg(textColor: string): string {
  if (textColor.includes('red'))     return 'bg-red-600';
  if (textColor.includes('orange'))  return 'bg-orange-500';
  if (textColor.includes('amber'))   return 'bg-amber-500';
  if (textColor.includes('emerald')) return 'bg-emerald-600';
  if (textColor.includes('blue'))    return 'bg-blue-600';
  if (textColor.includes('purple'))  return 'bg-purple-600';
  return 'bg-slate-600';
}

// Vehicle type label dari object_type (mis. "car" → "Car", "truck" → "Truck").
function vehicleTypeLabel(objectType: string | undefined | null): string {
  const shape = objectTypeToShape(objectType);
  return shape.charAt(0).toUpperCase() + shape.slice(1);
}

export function EventNotifier() {
  const navigate = useNavigate();
  const addEvent = useEventsStore((s) => s.add);
  const isNotifyAllowed = useEventsStore((s) => s.isNotifyAllowed);
  const setPendingPickId = useEventsStore((s) => s.setPendingPickId);
  const vehiclesById = useVehiclesStore((s) => s.byId);

  // --- stack popup sendiri (pengganti sonner untuk notifikasi event/command) ---
  const [stack, setStack] = useState<StackItem[]>([]);
  const nextIdRef = useRef(1);
  const dismiss = useCallback((id: number) => {
    setStack((s) => s.filter((it) => it.id !== id));
  }, []);
  const push = useCallback(
    (render: StackItem['render']) => {
      const id = nextIdRef.current++;
      // Terbaru di DEPAN array → tampil paling atas kolom kanan; yang lama
      // terdorong ke bawah lalu wrap ke kolom kiri.
      setStack((s) => [{ id, render }, ...s].slice(0, MAX_STACK));
      window.setTimeout(() => dismiss(id), POPUP_DURATION_MS);
    },
    [dismiss],
  );

  const getVehicleName = useCallback(
    (id: number) => {
      const v = vehiclesById.get(id);
      return v?.nopol || v?.real_name || '';
    },
    [vehiclesById],
  );

  const handle = useCallback(
    (msg: WsMessage) => {
      const ev = extractEvent(msg as AlarmWsPayload, getVehicleName);
      if (!ev) return;

      addEvent(ev);

      // Notification + sound HANYA untuk alarmId yg user pilih di sidebar
      // (notifyAlarmIds). Empty whitelist = allow all (default behavior).
      // List event tetap tampilkan semua — filter ini independent dari display.
      if (!isNotifyAllowed(ev.alarmId)) return;

      const vehicle = vehiclesById.get(ev.vehicleId);
      const { Icon: AlarmIcon, color: alarmColor } = alarmVisual(ev.alarmId);
      const headerBg = alarmHeaderBg(alarmColor);
      const objectLabel = vehicleTypeLabel(vehicle?.object_type);

      push((close) => (
        <div className="bg-card border border-border rounded-md shadow-xl overflow-hidden">
          {/* Header bar — warna sesuai severity alarm */}
          <div className={cn('flex items-center justify-between px-3 py-1.5 text-white', headerBg)}>
            <div className="flex items-center gap-2">
              <AlarmIcon className="w-4 h-4" />
              <span className="font-semibold text-sm">New event</span>
            </div>
            <button
              type="button"
              onClick={close}
              aria-label="Close"
              className="opacity-80 hover:opacity-100"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          {/* Body — Object / Event / Position / Time */}
          <div className="p-3 text-xs space-y-1.5">
            <Row label="Object"   value={objectLabel} />
            <Row label="Event"    value={ev.geofence ? `${ev.alarmName} · ${ev.geofence}` : ev.alarmName} valueClass={cn('font-medium', alarmColor)} />
            <Row label="Position" value={`${ev.lat.toFixed(6)}, ${ev.lng.toFixed(6)}`} valueClass="tabular-nums text-foreground" />
            <Row label="Time"     value={formatDateTime(ev.timestamp)} valueClass="tabular-nums text-foreground" />
            {ev.vehicleName && <Row label="Vehicle" value={ev.vehicleName} />}
            <div className="pt-1">
              <button
                type="button"
                onClick={() => {
                  setPendingPickId(ev.id);
                  navigate('/monitoring');
                  close();
                }}
                className="text-xs text-primary hover:underline font-medium"
              >
                Show event →
              </button>
            </div>
          </div>
        </div>
      ));
      playAlarmSound();
    },
    [addEvent, isNotifyAllowed, getVehicleName, vehiclesById, navigate, setPendingPickId, push],
  );

  useWebSocketEvent('alarm', handle);
  // Beberapa backend kirim alarm via type 'gps' dengan field alarm>0;
  // kita tangkap juga supaya tidak miss.
  useWebSocketEvent('gps', handle);

  // Command response popup — WS type='response' (lihat
  // BroadcastCommandResponse di websocket.go).
  useWebSocketEvent('response', useCallback((msg: WsMessage) => {
    const m = msg as Record<string, unknown>;
    const cmdName  = String(m.command_name ?? m.cmdid ?? 'command');
    const status   = String(m.status ?? 'responded');
    // Raw response text bisa berisi binary GPS protocol bytes (GT06/Wanway
    // hex frame) yang ke-render sebagai mojibake. Sanitize: kalau rasio
    // printable ASCII di bawah threshold, kosongkan supaya tidak tampil
    // garbled di toast. response_text > msg > cmd_info — prefer field yang
    // explicitly named.
    const rawResp  = String(m.response_text ?? m.msg ?? m.cmd_info ?? '');
    const respText = sanitizeRespText(rawResp);
    const vhId     = Number(m.vehicle_id ?? 0);
    const vName    = vhId ? (getVehicleName(vhId) || `#${vhId}`) : '';
    const success  = status === 'ok' || status === 'success' || status === 'responded';
    const Icon     = success ? CheckCircle2 : (status === 'failed' || status === 'error' ? XCircle : Send);
    const color    = success ? 'text-emerald-600' : (status === 'failed' || status === 'error' ? 'text-destructive' : 'text-primary');
    // Kalau cmdName generic + respText binary → skip toast sama sekali.
    // User tidak butuh notifikasi "device sent something incomprehensible".
    const isGeneric = cmdName === 'command' || cmdName === '';
    if (isGeneric && !respText) return;
    push((close) => (
      <div className="bg-card border border-border rounded-md shadow-xl p-3 flex items-start gap-2">
        <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${color}`} />
        <div className="min-w-0 flex-1">
          <div className="font-medium text-sm">
            {cmdName}
            <span className="text-[10px] text-muted-foreground ml-1.5 uppercase">{status}</span>
          </div>
          {vName && <div className="text-xs text-muted-foreground">{vName}</div>}
          {respText && (
            <div className="text-xs mt-0.5 break-words font-mono">{respText}</div>
          )}
        </div>
        <button type="button" onClick={close} aria-label="Close" className="opacity-60 hover:opacity-100 shrink-0">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    ));
  }, [getVehicleName, push]));

  // Stack popup: anchor kanan-atas. flex-col + flex-wrap-reverse → item mengisi
  // kolom MENURUN; saat kolom mencapai max-height, kolom berikutnya muncul DI
  // KIRI kolom sebelumnya (wrap-reverse membalik arah cross-axis), jadi semua
  // notifikasi terbaca tanpa overlap. pointer-events-none di container supaya
  // area kosong tetap tembus klik ke peta; tiap kartu pointer-events-auto.
  return (
    <div className="fixed top-4 right-4 z-[100] flex max-h-[calc(100vh-2rem)] max-w-[calc(100vw-2rem)] flex-col flex-wrap-reverse content-start gap-2 pointer-events-none">
      {stack.map((it) => (
        <div key={it.id} className="pointer-events-auto w-[320px]">
          {it.render(() => dismiss(it.id))}
        </div>
      ))}
    </div>
  );
}

// sanitizeRespText — backend kadang broadcast field `msg` yang isinya raw
// binary GPS protocol bytes (GT06/Wanway hex frame). Saat di-decode sebagai
// JSON string, byte ber-MSB tinggi (>0x7F) ke-render sebagai karakter
// Mandarin/Latin extended mojibake. Hitung rasio karakter ASCII printable;
// kalau di bawah 70%, treat as binary garbage dan return string kosong.
function sanitizeRespText(s: string): string {
  if (!s) return '';
  const trimmed = s.trim();
  if (!trimmed) return '';
  let printable = 0;
  for (let i = 0; i < trimmed.length; i++) {
    const code = trimmed.charCodeAt(i);
    // Printable ASCII (space..~) + tab/newline/CR.
    if ((code >= 0x20 && code <= 0x7E) || code === 0x09 || code === 0x0A || code === 0x0D) {
      printable++;
    }
  }
  if (printable / trimmed.length < 0.7) return '';
  // Strip control char yang lolos (NUL, BEL, dst) tapi pertahankan newline.
  // eslint-disable-next-line no-control-regex
  return trimmed.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '').trim();
}

function Row({ label, value, valueClass }: { label: string; value: string; valueClass?: string }) {
  return (
    <div className="flex items-baseline gap-3">
      <span className="text-muted-foreground w-16 shrink-0 text-[10px] uppercase tracking-wide">{label}</span>
      <span className={cn('flex-1 truncate', valueClass)}>{value}</span>
    </div>
  );
}
